import { FSM_STATES } from '../../context/defaultState'
import { RestorableRoundData } from '../../context/session/types'
import { Assignation, GO_BACK_INDEX, SearchParams } from '../../context/types'
import { info, logException } from '../../services/sentryLogger'
import { stm2str, stms2str } from '../../utils/stm'
import { FixedArray } from '../../utils/types'
import _ from 'lodash'

export async function nArySort<T, N extends number>(
    p: SearchParams<T, N>
): Promise<T[]> {
    info('============ nArySort()', 'init')
    let {
        asgnsLeft2Rank,
        asgnsRanked,
        currentStep: savedStep,
        maxComparedSize,
        setAndSaveSessionData,
    } = p

    let insertionIndex = -1
    p.currentStep = savedStep ?? 1
    let maxStep = asgnsLeft2Rank.length - 1 + (p.currentStep - 1)
    // INIT
    if (!asgnsRanked.length) {
        info(
            'nArySort',
            'init 0 (no asgn ranked)',
            asgnsLeft2Rank.length,
            asgnsRanked.length,
            {
                currentStep: p.currentStep,
                savedStep,
                maxComparedSize,
            },
            'asgnsLeft2Rank',
            stms2str(asgnsLeft2Rank as Assignation[]),
            'asgnsRanked',
            stms2str(asgnsRanked as Assignation[])
        )
        // atomic swap - step = 0
        asgnsRanked.push(asgnsLeft2Rank.shift() as T)
    } else {
        info(
            'nArySort',
            'resume (some asgn ranked)',
            asgnsLeft2Rank.length,
            asgnsRanked.length,
            {
                currentStep: p.currentStep,
                savedStep,
                maxComparedSize,
            },
            'asgnsLeft2Rank',
            stms2str(asgnsLeft2Rank as Assignation[]),
            'asgnsRanked',
            stms2str(asgnsRanked as Assignation[])
        )
    }

    // LOOP - Empty asgnsLeft2Rank
    while (asgnsLeft2Rank.length > 0) {
        const currentItem = asgnsLeft2Rank.at(0) as T // top() not pop/shift() !
        info(
            '========= nArySort()',
            `step ${p.currentStep}/${maxStep}`,
            {
                currentItem,
            },
            'asgnsLeft2Rank',
            stms2str(asgnsLeft2Rank),
            'asgnsRanked',
            stms2str(asgnsRanked)
        )
        const startIndex = 0
        const endIndex = p.currentStep
        insertionIndex = await nArySearch(p, currentItem, startIndex, endIndex)
        if (insertionIndex === GO_BACK_INDEX) {
            info('========= nArySort()', 'resolve(GO_BACK_INDEX)', {
                insertionIndex,
            })
            if (p.currentStep <= 1) {
                logException('nary', 'Cannot go back from here!')
                p.currentStep = -1
            } else {
                logException('nary', 'Unknown GO_BACK_INDEX')
                p.currentStep = -2
                continue
            }
        } else {
            info(
                'nArySort()',
                'resolve(insertionIndex)',
                { insertionIndex },
                'asgnsRanked',
                stms2str(asgnsRanked as Assignation[]),
                'asgnsLeft2Rank',
                stms2str(asgnsLeft2Rank as Assignation[])
            )
            // atomic swap
            asgnsRanked.splice(insertionIndex, 0, asgnsLeft2Rank.shift() as T)
            info(
                '========= nArySort',
                'resolve(insertionIndex)',
                'asgnsRanked',
                stms2str(asgnsRanked as Assignation[])
            )
            setAndSaveSessionData((sd) => ({
                ...sd,
                restorable: {
                    ...sd.restorable,
                    fsmState: FSM_STATES.RANK_14_X_BY_X,
                    savedRounds: sd.restorable.savedRounds.map((r, i) => {
                        return i === p.currentRoundIndex
                            ? ({
                                  ...r,
                                  currentStmIndex: p.currentStep - 1 + 1, // ok+1
                                  partiallyRankedRound:
                                      asgnsRanked.map((a: any) => a.valueKey),
                              } as RestorableRoundData)
                            : r
                    }),
                },
            }))
        }
        p.currentStep = p.currentStep + 1
    }
    return asgnsRanked
}

async function nArySearch<T, N extends number>(
    p: SearchParams<T, N>,
    // recursive variables:
    currentItem: T,
    start: number,
    end: number // not included !
): Promise<number> {
    const { asgnsRanked, maxComparedSize, compareNAry, currentStep } = p
    if (!currentItem) throw new Error('currentItem is undefined')
    let outputIndex = -1
    let intervalSize = end - start
    // FIXME readonly T[]
    let intervalItems = asgnsRanked.slice(start, end) as FixedArray<
        T,
        typeof maxComparedSize
    >
    info(
        '>>> nArySearch',
        {
            currentStep,
            maxComparedSize,
            start,
            end,
            intervalSize,
            currentItem: stm2str(currentItem),
        },
        'intervalItems',
        stms2str(intervalItems),
        'asgnsRanked',
        stms2str(asgnsRanked as Assignation[])
    )
    if (intervalSize === 0) {
        outputIndex = start
    } else if (intervalSize === 1) {
        // leaf terminal case
        info(
            'before compareNAry()',
            {
                currentStep,
                currentItem: stm2str(currentItem),
                intervalSize,
            },
            'intervalItems',
            stms2str(intervalItems)
        )
        const insertionIndex = await compareNAry(
            currentItem,
            intervalItems,
            currentStep // for UI
        )
        if (insertionIndex === GO_BACK_INDEX) {
            info('>> compareNAry', currentStep, 'resolve(GO_BACK_INDEX)', {
                insertionIndex,
            })
            return GO_BACK_INDEX
        } else {
            info('>> compareNAry', currentStep, 'resolve(idx)', {
                insertionIndex,
                intervalSize,
            })
        }
        outputIndex = start + insertionIndex
    } else if (intervalSize <= maxComparedSize) {
        // leaf terminal case
        info(
            'before compareNAry()',
            {
                currentStep,
                currentItem: stm2str(currentItem),
                intervalSize,
            },
            'intervalItems',
            stms2str(intervalItems)
        )
        const insertionIdx = await compareNAry(
            currentItem,
            intervalItems,
            currentStep
        )
        if (insertionIdx === GO_BACK_INDEX) {
            info('>> compareNAry', currentStep, 'resolve(GO_BACK_INDEX)', {
                insertionIdx,
            })
            return GO_BACK_INDEX
        } else {
            info('>> compareNAry', currentStep, 'resolve(idx)', {
                insertionIdx,
                intervalSize,
            })
        }
        // FIXME n!==2
        switch (insertionIdx) {
            // FIXME harcoded placeholder for n=2
            case 0:
                // before pivot1
                outputIndex = start
                break
            case 2:
                // between pivot1 and pivot2
                outputIndex = start + 1
                break
            case 4:
                // after pivot2
                outputIndex = end
                break
        }
        info('after compareNAry()', {
            currentStep,
            intervalSize,
            outputIndex,
            insertionIdx,
        })
    } else {
        const intervalsWPivot = divideWPivots(
            maxComparedSize,
            intervalSize,
            start
        )
        const pivotsValues = intervalsWPivot.map(
            (pi) => asgnsRanked[pi.pivotIndex]
        ) as FixedArray<T, typeof maxComparedSize>
        info(
            '>> subInterval compare()',
            {
                currentStep,
                currentItem: stm2str(currentItem),
                intervalSize,
                maxComparedSize,
                intervalsWPivot,
            },
            'pivotsValues',
            stms2str(pivotsValues),
            'intervalItems',
            stms2str(intervalItems)
        )
        const intervalIndex = await compareNAry(
            currentItem,
            pivotsValues,
            currentStep
        )
        info('>> subInterval compare()', { currentStep, intervalIndex })
        if (intervalIndex === GO_BACK_INDEX) {
            info('>> compareNAry', currentStep, 'resolve(GO_BACK_INDEX)', {
                intervalIndex,
            })
            return GO_BACK_INDEX
        } else {
            info('>> compareNAry', currentStep, 'resolve(idx)', {
                intervalIndex,
                intervalSize,
            })
        }
        let newEnd = -1
        let newStart = -1
        if (maxComparedSize === 2) {
            const pivot1 = intervalsWPivot[0].pivotIndex
            const pivot2 = intervalsWPivot[1].pivotIndex
            switch (intervalIndex) {
                // FIXME harcoded placeholder for n=3
                case 0:
                    // before pivot1
                    newStart = start
                    newEnd = Math.max(pivot1, newStart)
                    // if(newEnd === newStart) return newStart;
                    break
                case 2:
                    // between pivot1 and pivot2
                    newStart = Math.min(pivot1 + 1, end)
                    newEnd = Math.max(newStart, pivot2 - 1)
                    // if(newEnd === newStart) return newStart;
                    break
                case 4:
                    // after pivot2
                    newStart = Math.min(pivot2 + 1, end)
                    newEnd = end
                    // if(newEnd === newStart) return newStart;
                    break
                default:
                    logException(
                        'nary',
                        'Invalid intervalIndex= ' + intervalIndex
                    )
            }
        } else if (maxComparedSize === 1) {
            const pivot = intervalsWPivot[0].pivotIndex
            switch (intervalIndex) {
                case 0:
                    // before pivot
                    newStart = start
                    newEnd = Math.max(pivot, newStart)
                    break
                case 1:
                    // after pivot
                    newStart = Math.min(pivot + 1, end)
                    newEnd = end
                    break
                default:
                    logException(
                        'nary',
                        'Invalid intervalIndex= ' + intervalIndex
                    )
            }
        }
        info('before nArySearch()', {
            currentStep,
            intervalSize,
            intervalIndex,
            intervalsWPivot,
            newStart,
            newEnd,
            start,
            end,
        })
        // iterate
        outputIndex = await nArySearch(p, currentItem, newStart, newEnd)
        if (intervalIndex === GO_BACK_INDEX) {
            info('>> compareNAry', currentStep, 'resolve(GO_BACK_INDEX)', {
                outputIndex,
            })
            return GO_BACK_INDEX
        } else {
            info('>> compareNAry', currentStep, 'resolve(idx)', {
                outputIndex,
                intervalSize,
            })
        }
        info('>>> BSearch non terminal=', {
            currentStep,
            intervalSize,
            outputIndex,
        })
    }
    return outputIndex
}

/**
 *
 * @param n
 * @param intervalSize
 * @returns
 */
const divideWPivots = (
    n: number,
    intervalSize: number,
    startIntervalIdx: number = 0
) => {
    const pivotsIndices = getEvenlySpreadIndices(intervalSize, n)
    const intervals = pivotsIndices.map((pivotIndex, i) => {
        const start = (i === 0 ? 0 : pivotsIndices[i - 1]) + startIntervalIdx
        const end =
            (i === n - 1 ? intervalSize - 1 : Math.min(0, pivotIndex - 1)) +
            startIntervalIdx
        return { start, end, pivotIndex: pivotIndex + startIntervalIdx }
    })
    return intervals
}

/**
 *
 * @param intervalSize
 * @param n
 * @returns
 */
function getEvenlySpreadIndices(intervalSize: number, n: number): number[] {
    // Check if k is valid
    if (n <= 0 || n > intervalSize) {
        throw new Error('Invalid value of n')
    }

    const result: number[] = []
    const binSize = intervalSize / n
    info('getEvenlySpreadIndices', { binSize, n })

    for (let i = 0; i < n; i++) {
        // Calculate the start and end indices of the bin
        const start = Math.floor(i * binSize)
        const end = Math.min(Math.floor((i + 1) * binSize), intervalSize - 1)

        // Find the middle index of the bin
        const middleIndex = Math.min(
            Math.ceil((start + end) / 2), // FIXME floor
            intervalSize - 1
        )
        info('getEvenlySpreadIndices', i, { start, middleIndex, end })
        result.push(middleIndex)
    }

    return result
}
